import React from 'react';
import { Table, Tooltip } from 'antd';
import { Tag } from 'appkit-react';
import DDNumber from '@utils/number';
import { addHTTP } from '@utils/common';
import {
  financialImpactTip,
  oneTimeCostTip,
  probabilityTip,
  timeToAchieveTip,
} from '../valueDriverManager/tips';
import RichEditor from '@modules/questionnaires/complexity/richEditor';

// import '@style/modules/insights/valueDrivers.scss';
import '../../../styles/modules/insights/valueDrivers.scss';
import { VALUEDRIVER_OPTIONS } from '@model';
import useTheme from '@hooks/useTheme';

const Tip = ({ text }) => (
  <Tooltip title={text} placement="bottomRight">
    <span className="appkiticon icon-information-fill dd-tip-icon" />
  </Tooltip>
);

const HyperLink = ({ url }) => (
  <Tooltip
    title={
      <a href={addHTTP(url, 'http://')} target="_Blank" rel="noreferrer">
        {url}
      </a>
    }
  >
    <span
      className="appkiticon icon-link-fill dd-tip-icon"
      style={{ paddingLeft: '0.5rem' }}
    />
  </Tooltip>
);

const tagBackgroundBlueInLightMode = {
  'Cost Synergy': '#4DACF1',
  'Revenue Synergy': '#0089EB',
  'Capital Optimization Synergy': '#0060D7',
};

const tagBackgroundBlue = {
  'Cost Synergy': '#69A3C9',
  'Revenue Synergy': '#269BEE',
  'Capital Optimization Synergy': '#006CE2',
};

const ValueDriversGrid = ({ dataSrc, format, mode, chartHidden }) => {
  // const isScaleMode = mode === 'scale';
  const formatNumber =
    VALUEDRIVER_OPTIONS.format.find((i) => i.label === format)?.value || 1;
  const applyCommonProperties = (ele) =>
    Object.assign(ele, {
      align: 'left',
    });
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  const combineToTag = (rec) => `${rec.synergy} ${rec.type}`;

  const isRecSynergy = (rec) => combineToTag(rec).indexOf('Dis-Synergy') === -1;

  const tagBlueColorRange = isLightMode
    ? tagBackgroundBlueInLightMode
    : tagBackgroundBlue;
  const applyTagColorBy = (rec) =>
    !isRecSynergy(rec)
      ? '#E0301E'
      : tagBlueColorRange[`${combineToTag(rec)}`] || '#000000';

  const tableCols = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      // width: '30rem',
      render: (_, rec) => (
        <>
          <Tag
            size="sm"
            className="name-tag"
            style={{ backgroundColor: applyTagColorBy(rec) }}
          >
            {combineToTag(rec)}
          </Tag>
          <span className="name-text">
            {rec.category}
            {rec.hasLinkUrl ? <HyperLink url={rec.linkUrl} /> : <div />}
          </span>
        </>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render(text) {
        try {
          const description = JSON.parse(text);
          return typeof JSON.parse(text) === 'object' ? (
            <RichEditor value={JSON.parse(text)} readOnly />
          ) : (
            description
          );
        } catch (e) {
          console.log(e);
          return text;
        }
      },
    },
    {
      title: (
        <div>
          Probability of Success <Tip text={probabilityTip} />
        </div>
      ),
      dataIndex: 'probabilityOfSuccess',
      key: 'probabilityOfSuccess',
      width: '9rem',
    },
    {
      title: (
        <div>
          Time to Achieve <Tip text={timeToAchieveTip} />
        </div>
      ),
      dataIndex: 'timeToAchieve',
      key: 'timeToAchieve',
      width: '9rem',
    },
    {
      title: (
        <div>
          Financial Impact <Tip text={financialImpactTip(format)} />
        </div>
      ),
      dataIndex: 'financialImpactOfDollarValue',
      key: 'financialImpactOfDollarValue',
      width: '9rem',
      render: (_, rec) => {
        if (Number.isNaN(parseInt(rec.financialImpactOfDollarValue))) {
          return rec.financialImpactOfScale;
        }
        if (
          rec.financialImpactOfDollarValue == null ||
          typeof rec.financialImpactOfDollarValue === 'undefined'
        ) {
          return '';
        }
        let finImpact = rec.financialImpactOfDollarValue;
        if (!isRecSynergy(rec)) {
          finImpact *= -1;
        }
        if (mode === 'scale') {
          finImpact /= formatNumber;
        }
        return DDNumber.formatWithoutSuffix(finImpact);
      },
    },
    {
      title: (
        <div>
          One-time Cost to Achieve <Tip text={oneTimeCostTip(format)} />
        </div>
      ),
      dataIndex: 'oneTimeCostToAchieveOfDollarValue',
      key: 'oneTimeCostToAchieveOfDollarValue',
      width: '9rem',
      render: (_, rec) => {
        let oneTimeCostToAchieveOfDollarValue =
          rec.oneTimeCostToAchieveOfDollarValue;
        if (
          oneTimeCostToAchieveOfDollarValue == null &&
          (rec.oneTimeCostToAchieveOfScale == null ||
            String(rec.oneTimeCostToAchieveOfScale).toLowerCase() === 'tbd')
        ) {
          return isRecSynergy(rec) ? 'TBD' : '-';
        }

        if (rec.oneTimeCostToAchieveOfScale === 'none') {
          return '-';
        }

        if (Number.isNaN(parseInt(oneTimeCostToAchieveOfDollarValue))) {
          return rec.oneTimeCostToAchieveOfScale;
        }

        if (mode === 'scale') {
          oneTimeCostToAchieveOfDollarValue /= formatNumber;
        }
        return DDNumber.formatWithoutSuffix(
          oneTimeCostToAchieveOfDollarValue * -1,
        );
      },
    },
  ].map(applyCommonProperties);
  return (
    <div className="value-dirvers-grid">
      <Table
        dataSource={dataSrc}
        columns={tableCols}
        pagination={false}
        scroll={{ y: chartHidden ? 400 : 300 }}
      />
    </div>
  );
};

export default ValueDriversGrid;
